import { FeedLetConfig } from './types'

export class FloatingButton {
  private element: HTMLElement | null = null
  private config: FeedLetConfig
  private onFeedbackClick: (() => void) | null = null
  private onContextualClick: (() => void) | null = null
  private isContextualMode = false

  constructor(config: FeedLetConfig) {
    this.config = config
  }

  create(
    onFeedbackClick: () => void,
    onContextualClick?: () => void
  ): HTMLElement {
    this.onFeedbackClick = onFeedbackClick
    this.onContextualClick = onContextualClick || null

    // Create a sticky wrapper
    const stickyWrapper = document.createElement('div')
    stickyWrapper.className = 'feedlet-sticky-wrapper'
    
    this.element = document.createElement('div')
    this.element.id = 'feedlet-floating-button'
    this.element.className = 'feedlet-floating-container'

    this.updateElement()
    this.addStyles()
    this.attachEventListeners()

    stickyWrapper.appendChild(this.element)
    return stickyWrapper
  }

  private updateElement() {
    if (!this.element) return

    const enableContextual = this.config.enableContextualFeedback !== false

    this.element.innerHTML = `
      <div class="feedlet-social-bar">
        ${enableContextual ? `
          <button 
            id="feedlet-contextual-fab" 
            class="feedlet-social-btn ${this.isContextualMode ? 'active' : ''}"
            title="Click to add contextual comments"
          >
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"/>
            </svg>
            <span class="feedlet-social-label">Add Comment</span>
          </button>
        ` : ''}
        
        <button 
          id="feedlet-feedback-fab" 
          class="feedlet-social-btn"
          title="Send feedback"
        >
          <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clip-rule="evenodd"/>
          </svg>
          <span class="feedlet-social-label">Feedback</span>
        </button>
      </div>
    `
  }

  private addStyles() {
    if (document.getElementById('feedlet-floating-styles')) return

    const style = document.createElement('style')
    style.id = 'feedlet-floating-styles'
    style.textContent = `
      .feedlet-sticky-wrapper {
        position: fixed;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        z-index: 999999;
        pointer-events: none;
      }

      .feedlet-floating-container {
        position: relative;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        pointer-events: auto;
      }

      .feedlet-social-bar {
        display: flex;
        flex-direction: column;
        gap: 0;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        border-radius: 8px 0 0 8px;
        overflow: hidden;
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-right: none;
      }

      .feedlet-social-btn {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: 16px 12px;
        border: none;
        background: ${this.config.color || '#4f46e5'};
        color: white;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
        min-width: 50px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        gap: 8px;
      }

      .feedlet-social-btn:last-child {
        border-bottom: none;
      }

      .feedlet-social-btn:hover {
        background: ${this.adjustColor(this.config.color || '#4f46e5', 20)};
        transform: translateX(-2px);
        min-width: 120px;
      }

      .feedlet-social-btn.active {
        background: ${this.adjustColor(this.config.color || '#4f46e5', 40)};
        transform: translateX(-2px);
        min-width: 120px;
      }

      .feedlet-social-btn:active {
        transform: translateX(-1px) scale(0.98);
      }

      .feedlet-social-label {
        font-size: 14px;
        font-weight: 500;
        opacity: 0;
        transform: translateX(-10px);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        white-space: nowrap;
        margin-left: 4px;
      }

      .feedlet-social-btn:hover .feedlet-social-label,
      .feedlet-social-btn.active .feedlet-social-label {
        opacity: 1;
        transform: translateX(0);
      }

      /* Ripple effect */
      .feedlet-social-btn::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 0;
        height: 0;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.3);
        transform: translate(-50%, -50%);
        transition: width 0.6s, height 0.6s;
      }

      .feedlet-social-btn:active::before {
        width: 120px;
        height: 120px;
      }

      /* Alternative color schemes for different buttons */
      .feedlet-social-btn:nth-child(1) {
        background: #1DA1F2; /* Twitter blue for comment */
      }

      .feedlet-social-btn:nth-child(1):hover {
        background: #1a91da;
      }

      .feedlet-social-btn:nth-child(2) {
        background: ${this.config.color || '#4f46e5'}; /* Keep config color for feedback */
      }

      .feedlet-social-btn:nth-child(2):hover {
        background: ${this.adjustColor(this.config.color || '#4f46e5', 20)};
      }

      /* Mobile optimizations */
      @media (max-width: 768px) {
        .feedlet-social-btn {
          padding: 14px 10px;
          min-width: 44px;
        }
        
        .feedlet-social-btn:hover {
          min-width: 100px;
        }
        
        .feedlet-social-label {
          font-size: 12px;
        }
      }

      /* Pulse animation for contextual mode */
      .feedlet-social-btn.active {
        animation: feedlet-pulse 2s infinite;
      }

      @keyframes feedlet-pulse {
        0% { box-shadow: 0 0 0 0 rgba(29, 161, 242, 0.7); }
        70% { box-shadow: 0 0 0 10px rgba(29, 161, 242, 0); }
        100% { box-shadow: 0 0 0 0 rgba(29, 161, 242, 0); }
      }
    `

    document.head.appendChild(style)
  }

  private adjustColor(color: string, amount: number): string {
    const usePound = color[0] === '#'
    const col = usePound ? color.slice(1) : color
    const num = parseInt(col, 16)
    
    let r = (num >> 16) + amount
    let g = (num >> 8 & 0x00FF) + amount
    let b = (num & 0x0000FF) + amount
    
    r = r > 255 ? 255 : r < 0 ? 0 : r
    g = g > 255 ? 255 : g < 0 ? 0 : g
    b = b > 255 ? 255 : b < 0 ? 0 : b
    
    return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0')
  }

  private attachEventListeners() {
    if (!this.element) return

    // Contextual/Comment button
    const contextualFab = this.element.querySelector('#feedlet-contextual-fab')
    if (contextualFab && this.onContextualClick) {
      contextualFab.addEventListener('click', () => {
        if (this.onContextualClick) {
          this.onContextualClick()
        }
      })
    }

    // Feedback button
    const feedbackFab = this.element.querySelector('#feedlet-feedback-fab')
    if (feedbackFab && this.onFeedbackClick) {
      feedbackFab.addEventListener('click', () => {
        if (this.onFeedbackClick) {
          this.onFeedbackClick()
        }
      })
    }
  }

  show() {
    if (this.element) {
      this.element.style.display = 'block'
    }
  }

  hide() {
    if (this.element) {
      this.element.style.display = 'none'
    }
  }

  destroy() {
    if (this.element) {
      this.element.remove()
      this.element = null
    }

    const styles = document.getElementById('feedlet-floating-styles')
    if (styles) {
      styles.remove()
    }
  }

  updateContextualMode(isEnabled: boolean) {
    this.isContextualMode = isEnabled
    this.updateElement()
    // Re-attach event listeners after updating the element
    this.attachEventListeners()
  }
} 